import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  getNotificationsService,
  markNotificationAsReadService,
} from "../../../services/notifications";

const MessageDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchMessage = async () => {
      setIsLoading(true);
      setError(false);
      try {
        // Fetch notifications and find the one matching the ID
        const response = await getNotificationsService(1, 100);
        const items =
          response?.data?.notifications ||
          response?.data ||
          response?.notifications ||
          [];
        const found = Array.isArray(items)
          ? items.find((n) => n._id === id)
          : null;

        if (found) {
          setMessage(found);
          // Mark as read
          if (!found.read) {
            try {
              await markNotificationAsReadService(found._id);
            } catch (e) {
              // silent fail for marking read
            }
          }
        } else {
          setError(true);
        }
      } catch (err) {
        console.error("Error fetching message:", err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchMessage();
    }
  }, [id]);

  if (isLoading) {
    return (
      <div className="w-full max-w-4xl mx-auto mt-4 px-4 pb-12">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden p-8">
          <Skeleton height={24} width={120} className="mb-4" />
          <Skeleton height={36} className="mb-6" />
          <div className="flex items-center gap-3 mb-8">
            <Skeleton circle height={40} width={40} />
            <div>
              <Skeleton height={16} width={150} />
              <Skeleton height={12} width={200} className="mt-1" />
            </div>
          </div>
          <Skeleton count={6} className="mb-2" />
        </div>
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-gray-600">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21.75 9v.906a2.25 2.25 0 01-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 001.183 1.981l6.478 3.488m8.839 2.51l-4.66-2.51m0 0l-1.023-.55a2.25 2.25 0 00-2.134 0l-1.022.55m0 0l-4.661 2.51m16.5 1.615a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V8.844a2.25 2.25 0 011.183-1.98l7.5-4.04a2.25 2.25 0 012.134 0l7.5 4.04a2.25 2.25 0 011.183 1.98V18"
            />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-slate-700 mb-2">
          Message not found
        </h2>
        <p className="text-sm text-slate-400 mb-6">
          This message may have been deleted or is no longer available.
        </p>
        <button
          onClick={() => navigate("/")}
          className="px-5 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const typeBadge = message.type || "Message";
  const messageBody = message.message || "";
  const description = message.description || "";

  return (
    <div className="w-full max-w-4xl mx-auto mt-4 px-4 pb-12">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Header Section */}
        <div className="p-6 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-3 py-1 rounded-full uppercase tracking-wider">
              {typeBadge}
            </span>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-6">
            {message.title || "No Title"}
          </h1>

          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold uppercase">
                {message.sender?.first_name
                  ? message.sender.first_name[0]
                  : "A"}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {message.sender?.first_name
                    ? `${message.sender.first_name} ${message.sender?.last_name || ""}`
                    : "Admin / System"}
                </p>
                <p className="text-xs text-slate-500">
                  {message.sender?.email || "Sender"}
                </p>
              </div>
            </div>

            <div className="text-right">
              <p className="text-sm font-semibold text-slate-700">
                {moment(message.createdAt).format("MMM DD, YYYY")}
              </p>
              <p className="text-xs text-slate-500">
                {moment(message.createdAt).format("hh:mm A")}
              </p>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-6 md:p-8 min-h-[300px] space-y-6">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400 mb-2">
              Message
            </h2>
            <p className="text-base text-slate-700 leading-relaxed whitespace-pre-wrap">
              {messageBody || "No message provided."}
            </p>
          </div>

          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400 mb-2">
              Description
            </h2>
            <p className="text-base text-slate-700 leading-relaxed whitespace-pre-wrap">
              {description || "No description provided."}
            </p>
          </div>

          {message.link ? (
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400 mb-2">
                Link
              </h2>
              <a
                href={message.link}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-green-700 hover:text-green-800 break-all underline"
              >
                {message.link}
              </a>
            </div>
          ) : null}

        </div>
      </div>
    </div>
  );
};

export default MessageDetails;
